import { Cell, Row } from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { useTableContext } from "../../context";
import { useCellEdit } from "./use-cell-edit";

const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

export default function CellDate<T>({
  row,
  cell,
}: {
  row: Row<T>;
  cell: Cell<T, unknown>;
}) {
  const { table } = useTableContext();

  const updateData = table?.options?.meta?.updateData;
  const isUpdating = table?.options?.meta?.isUpdating ?? false;
  const columnId = cell.column?.id;
  const cellValue = cell.getValue() as string | undefined;
  const formattedCellValue = formatDateForInput(cellValue);

  const [value, setValue] = useState(formattedCellValue);

  useEffect(() => {
    setValue(formatDateForInput(cellValue));
  }, [cellValue]);

  const { controlProps } = useCellEdit<T, HTMLInputElement>({
    row,
    cell,
    isChanged: () => value !== formattedCellValue,
    commit: () => {
      try {
        updateData?.(row, columnId, value || null);
      } catch (error) {
        console.error(error);
        setValue(formattedCellValue);
      }
    },
    restore: () => setValue(formattedCellValue),
    describeValue: () => value,
  });

  return (
    <input
      {...controlProps}
      type="date"
      value={value}
      onChange={(event) => {
        const newValue = event.target.value;
        setValue(newValue);
      }}
      disabled={isUpdating}
      className="w-full h-target px-2 py-1 border border-default rounded-sm focus:outline-none focus:ring-1 focus:ring-focus focus:border-focus disabled:bg-surface disabled:cursor-not-allowed"
    />
  );
}
